import React, { useState, useEffect } from 'react'
import {
  CurrencyRupeeIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CalendarIcon,
  UserIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import api from '../../services/api'

interface SubscriptionPlan {
  id: number
  name: string
  plan_type: string
  price_min: number
  price_max: number
  duration_days: number
  is_active: boolean
}

interface Subscription {
  id: number
  user_id: number
  plan_id: number
  status: string
  amount_paid: number
  auto_renew: boolean
  start_date: string
  end_date: string
  payment_id?: string
  created_at: string
  plan: SubscriptionPlan
}

const SubscriptionManagement: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    fetchSubscriptions()
  }, [])

  useEffect(() => {
    let filtered = subscriptions
    if (searchTerm) {
      filtered = filtered.filter(sub =>
        sub.plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(sub.user_id).includes(searchTerm) ||
        (sub.payment_id || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter)
    }
    setFilteredSubscriptions(filtered)
  }, [subscriptions, searchTerm, statusFilter])

  const fetchSubscriptions = async () => {
    try {
      setLoading(true)
      const res = await api.get('/subscriptions/admin/subscriptions')
      setSubscriptions(res.data)
    } catch (error) {
      console.error('Error fetching subscriptions:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-400'
      case 'expired': return 'bg-red-500/10 text-red-400'
      case 'cancelled': return 'bg-dark-700 text-gray-400'
      case 'pending': return 'bg-yellow-500/10 text-yellow-400'
      default: return 'bg-dark-700 text-gray-400'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircleIcon className="w-5 h-5 text-green-400" />
      case 'expired': return <XCircleIcon className="w-5 h-5 text-red-400" />
      case 'cancelled': return <XCircleIcon className="w-5 h-5 text-gray-400" />
      case 'pending': return <ClockIcon className="w-5 h-5 text-yellow-400" />
      default: return <ClockIcon className="w-5 h-5 text-gray-400" />
    }
  }

  const totalRevenue = subscriptions.reduce((sum, sub) => sum + Number(sub.amount_paid), 0)
  const activeRevenue = subscriptions.filter(s => s.status === 'active').reduce((sum, sub) => sum + Number(sub.amount_paid), 0)
  const activeCount = subscriptions.filter(s => s.status === 'active').length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Subscription Management</h1>
          <p className="text-gray-400 mt-1">Manage user subscriptions and plans</p>
        </div>
        <Button variant="outline" onClick={fetchSubscriptions}>
          <ArrowPathIcon className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">₹{totalRevenue.toLocaleString('en-IN')}</p>
            <p className="text-sm text-gray-400">Total Revenue</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">₹{activeRevenue.toLocaleString('en-IN')}</p>
            <p className="text-sm text-gray-400">Active Revenue</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-400">{activeCount}</p>
            <p className="text-sm text-gray-400">Active Subscribers</p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by plan, user ID, payment ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-dark-900 border border-dark-600 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </Card>

      {/* Subscriptions Table */}
      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-900 border-b border-dark-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Auto Renew</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {filteredSubscriptions.map((subscription) => (
                <tr key={subscription.id} className="hover:bg-dark-700">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-9 h-9 bg-blue-500/10 rounded-full flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="ml-3 text-sm text-white">User #{subscription.user_id}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-white">{subscription.plan.name}</div>
                    <div className="text-xs text-gray-400">{subscription.plan.plan_type}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {getStatusIcon(subscription.status)}
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(subscription.status)}`}>
                        {subscription.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-white">₹{Number(subscription.amount_paid).toLocaleString('en-IN')}</div>
                    {subscription.payment_id && (
                      <div className="text-xs text-gray-400 truncate max-w-[120px]">{subscription.payment_id}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-400">
                    <div className="flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-1" />
                      <div>
                        <div>{new Date(subscription.start_date).toLocaleDateString('en-IN')}</div>
                        <div className="text-xs">to {new Date(subscription.end_date).toLocaleDateString('en-IN')}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${subscription.auto_renew ? 'bg-green-500/10 text-green-400' : 'bg-dark-700 text-gray-400'}`}>
                      {subscription.auto_renew ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <Button size="sm" variant="ghost" onClick={() => { setSelectedSubscription(subscription); setShowModal(true) }}>
                      <EyeIcon className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredSubscriptions.length === 0 && (
            <div className="text-center py-12">
              <CurrencyRupeeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No subscriptions found</p>
            </div>
          )}
        </div>
      </Card>

      {/* Detail Modal */}
      {showModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black/60 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-dark-700 w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-dark-800">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-white">Subscription Details</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-200">
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400 mb-0.5">Subscription ID</p>
                <p className="text-white font-medium">#{selectedSubscription.id}</p>
              </div>
              <div>
                <p className="text-gray-400 mb-0.5">User ID</p>
                <p className="text-white font-medium">#{selectedSubscription.user_id}</p>
              </div>
              <div>
                <p className="text-gray-400 mb-0.5">Plan</p>
                <p className="text-white font-medium">{selectedSubscription.plan.name}</p>
                <p className="text-xs text-gray-400">{selectedSubscription.plan.plan_type} · {selectedSubscription.plan.duration_days} days</p>
              </div>
              <div>
                <p className="text-gray-400 mb-0.5">Status</p>
                <div className="flex items-center gap-2">
                  {getStatusIcon(selectedSubscription.status)}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedSubscription.status)}`}>
                    {selectedSubscription.status}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-gray-400 mb-0.5">Amount Paid</p>
                <p className="text-white font-medium">₹{Number(selectedSubscription.amount_paid).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-gray-400 mb-0.5">Auto Renew</p>
                <p className={selectedSubscription.auto_renew ? 'text-green-400' : 'text-gray-400'}>
                  {selectedSubscription.auto_renew ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <div>
                <p className="text-gray-400 mb-0.5">Period</p>
                <p className="text-white">{new Date(selectedSubscription.start_date).toLocaleDateString('en-IN')} — {new Date(selectedSubscription.end_date).toLocaleDateString('en-IN')}</p>
              </div>
              {selectedSubscription.payment_id && (
                <div>
                  <p className="text-gray-400 mb-0.5">Payment ID</p>
                  <p className="text-white font-mono text-xs break-all">{selectedSubscription.payment_id}</p>
                </div>
              )}
            </div>

            <div className="mt-6">
              <Button variant="outline" onClick={() => setShowModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscriptionManagement
